import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { cloneData, cloneDataOrNull, generateUniqueDatabaseId, isSameData } from '../../dto/data-model-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { CustomerProfile } from '../customer-profile.js';
import { TimeZone } from '../../../time/timezone.js';
import { Price } from '../price.js';
import { LocalizedString } from '../localized-string.js';
import { OpenAccessProtocol } from './open-access-protocol.js';
import { VersionRange } from '../../core/version-range.js';
import { EventOpenAccessProtocol } from './event-open-access-protocol.js';
import { ComponentList } from '../../../components/core/component-list.js';
import { EntityMixin } from '../../../components/core/entity-mixin.js';
import { toSafeFilename } from '../../../utility/string.js';
import { CustomerAccountBase } from '../customer-account-base.js';
import { IProductWithSerialNumber } from '../../../contracts/product-with-serial-number.js';
import { PaymentMethod } from '../payment-method.js';
import { isNullOrUndefined } from '../../../utility/null-safety.js';
import { SeatedOpenAccessProtocol } from './seated-open-access-protocol.js';
import { OpenAccessPassSeatHelperFactory } from '../../../utility/open-access-pass-seat-helper-factory.js';
import { StagedSeatedOpenAccessProtocol } from './staged-seated-open-access-pass-protocol.js';
import { Cardinal } from '../../core/cardinal.js';
import { OpenAccessPassVariationDetails } from './open-access-pass-variation-details.js';
import { BooleanOrListOfString } from '../../core/bool-or-list.js';
import { PassEventValidityRule } from './pass-event-validity-rule.js';
import { TrueTime } from '../../../time/true-time.js';

export enum OpenAccessPassStatus {
  Live = 'LIVE',
  Used = 'USED',
  Canceled = 'CANCELED',
  Refunded = 'REFUNDED',
}

export enum OpenAccessPassSource {
  Admin = 'Admin',
  Client = 'Client',
}

type OpenAccessPassCreateBaseArgs = {
  status: OpenAccessPassStatus,
  uniqueId: string,
  creatorId: string | null,
  businessRef: BusinessModuleRef,
  creationDate: dayjs.Dayjs,
  templateId: string,
  orderId: string,
  viaOrderId: string | null,
  viaProductId: string | null,
  viaGroupId: string | null,
  storagePath: string | null,
  originalName: LocalizedString,
  price: Price,
  originalPrice: Price | null,
  protocol: OpenAccessProtocol,
  customer: CustomerProfile | null,
  variation: OpenAccessPassVariationDetails | null,
  overrideName?: LocalizedString | null,
  usedAt?: dayjs.Dayjs | null,
  serialNumber?: string | null,
  paymentMethod?: PaymentMethod | null,
  source: OpenAccessPassSource,
  components?: ComponentList,
};

type OpenAccessPassCreateWithCodeArgs = OpenAccessPassCreateBaseArgs & {
  code: string,
};

type OpenAccessPassCreateWithScanCodeArgs = OpenAccessPassCreateBaseArgs & {
  scanCode: string,
};

// eslint-disable-next-line new-cap
export class OpenAccessPass extends EntityMixin(DataModel) implements IProductWithSerialNumber {
  static readonly TYPE_ID: string = '19c0c499c3a548b9b10d6dec4a082a75';
  static readonly SCHEMA_VERSION: number = 18;
  static readonly DEFAULT_VARIATION_ID: string = '__DEFAULT_VARIATION__';

  private _status: OpenAccessPassStatus;
  private _usedAt: dayjs.Dayjs | null;

  private constructor(
      status: OpenAccessPassStatus,
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly code: string,
      public readonly businessRef: BusinessModuleRef,
      public readonly creationDate: dayjs.Dayjs,
      public readonly templateId: string,
      public readonly orderId: string | null,
      public readonly viaOrderId: string | null,
      public readonly viaProductId: string | null,
      public readonly viaGroupId: string | null,
      public readonly storagePath: string | null,
      // Name of the template.
      public readonly originalName: LocalizedString,
      // Name the pass was created with.
      public readonly overrideName: LocalizedString | null,
      public readonly price: Price,
      public readonly originalPrice: Price | null,
      public readonly protocol: OpenAccessProtocol,
      public readonly customer: CustomerProfile | null,
      public readonly variation: OpenAccessPassVariationDetails | null,
      usedAt: dayjs.Dayjs | null,
      public readonly serialNumber: string | null,
      public readonly paymentMethod: PaymentMethod | null,
      public readonly source: OpenAccessPassSource,
      public readonly components: ComponentList,
  ) {
    super();
    this._status = status;
    this._usedAt = usedAt;
  }

  get status(): OpenAccessPassStatus {
    return this._status;
  }

  get usedAt(): dayjs.Dayjs | null {
    return this._usedAt;
  }

  get scanCode(): string {
    return this.code;
  }

  get $typeId(): string {
    return OpenAccessPass.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(10, OpenAccessPass.SCHEMA_VERSION);
  }

  static create(args: OpenAccessPassCreateWithCodeArgs): OpenAccessPass;
  static create(args: OpenAccessPassCreateWithScanCodeArgs): OpenAccessPass;
  static create(args: OpenAccessPassCreateWithCodeArgs | OpenAccessPassCreateWithScanCodeArgs): OpenAccessPass {
    const code = 'code' in args ? args.code : args.scanCode;
    return new OpenAccessPass(
        args.status,
        args.uniqueId,
        args.creatorId,
        code,
        args.businessRef,
        args.creationDate,
        args.templateId,
        args.orderId,
        args.viaOrderId,
        args.viaProductId,
        args.viaGroupId,
        args.storagePath,
        args.originalName,
        args.overrideName ?? null,
        args.price,
        args.originalPrice,
        args.protocol,
        args.customer,
        args.variation,
        args.usedAt ?? null,
        args.serialNumber ?? null,
        args.paymentMethod ?? null,
        args.source ?? null,
        args.components ?? new ComponentList(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPass {
    return new OpenAccessPass(
        toEnumFromString(MapUtils.get<string>(map, '$status'), OpenAccessPassStatus),
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getStringOrNull(map, 'creatorId'),
        MapUtils.get<string>(map, 'code'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<string>(map, 'templateId'),
        MapUtils.getOrNull<string>(map, 'orderId'),
        MapUtils.getOrNull<string>(map, 'viaOrderId'),
        MapUtils.getOrNull<string>(map, 'viaProductId'),
        MapUtils.getOrNull<string>(map, 'viaGroupId'),
        MapUtils.getOrNull<string>(map, 'storagePath'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'originalName')),
        context.fromDataOrNull<LocalizedString>(MapUtils.getOrNull<SerializedData>(map, 'overrideName')),
        Price.decode(MapUtils.get<string>(map, 'price')),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'originalPrice')),
        context.fromData<OpenAccessProtocol>(MapUtils.get<SerializedData>(map, 'protocol')),
        context.fromDataOrNull(MapUtils.getOrNull<SerializedData>(map, 'customer')),
        context.fromDataOrNull(MapUtils.getOrNull<SerializedData>(map, 'variation')),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'usedAt')),
        MapUtils.getStringOrNull(map, 'serialNumber'),
        toEnumFromStringOrNull(MapUtils.getOrNull<string>(map, 'paymentMethod'), PaymentMethod),
        toEnumFromString(MapUtils.get<string>(map, 'source'), OpenAccessPassSource),
        ComponentList.deserialize(context, MapUtils.get<SerializedData[]>(map, 'components')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      '$status': fromEnumAsString(this.status),
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'code': this.code,
      'scanCode': this.code,
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'templateId': this.templateId,
      'orderId': this.orderId,
      'viaOrderId': this.viaOrderId,
      'viaProductId': this.viaProductId,
      'viaGroupId': this.viaGroupId,
      'storagePath': this.storagePath,
      'originalName': context.toData(this.originalName),
      'overrideName': context.toDataOrNull(this.overrideName),
      'price': this.price.encode(),
      'originalPrice': this.originalPrice?.encode() ?? null,
      'protocol': context.toData(this.protocol),
      'protocolType': fromEnumAsString(this.protocol.type),
      'customer': context.toDataOrNull(this.customer),
      'customerId': this.customer?.uniqueId ?? null,
      'variation': context.toDataOrNull(this.variation),
      'variationId': this.variation?.uniqueId ?? null,
      'usedAt': context.encodeDateOrNull(this.usedAt),
      'serialNumber': this.serialNumber,
      'paymentMethod': this.paymentMethod,
      'source': fromEnumAsString(this.source),
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return new OpenAccessPass(
        this.status,
        this.uniqueId,
        this.creatorId,
        this.code,
        this.businessRef.clone(),
        TimeZone.cloneDate(this.creationDate),
        this.templateId,
        this.orderId,
        this.viaOrderId,
        this.viaProductId,
        this.viaGroupId,
        this.storagePath,
        this.originalName.clone(),
        this.overrideName?.clone() ?? null,
        this.price.clone(),
        this.originalPrice?.clone() ?? null,
        cloneData(this.protocol),
        cloneDataOrNull(this.customer),
        cloneDataOrNull(this.variation),
        TimeZone.cloneDateOrNull(this.usedAt),
        this.serialNumber,
        this.paymentMethod,
        this.source,
        this.components.clone(),
    ) as this;
  }

  isSame(other: OpenAccessPass): boolean {
    if(other === this) {
      return true;
    }

    return other.status === this.status &&
        other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.code === this.code &&
        isSameData(other.businessRef, this.businessRef) &&
        other.templateId === this.templateId &&
        other.orderId === this.orderId &&
        other.viaOrderId === this.viaOrderId &&
        other.viaProductId === this.viaProductId &&
        other.viaGroupId === this.viaGroupId &&
        other.storagePath === this.storagePath &&
        isSameData(other.originalName, this.originalName) &&
        isSameData(other.overrideName, this.overrideName) &&
        isSameData(other.price, this.price) &&
        isSameData(other.originalPrice, this.originalPrice) &&
        isSameData(other.protocol, this.protocol) &&
        isSameData(other.customer, this.customer) &&
        isSameData(other.variation, this.variation) &&
        other.usedAt === this.usedAt &&
        other.creationDate === this.creationDate &&
        other.serialNumber === this.serialNumber &&
        other.paymentMethod === this.paymentMethod &&
        other.source === this.source &&
        isSameData(other.components, this.components);
  }

  copyWith(data: Partial<OpenAccessPass>): this {
    return new OpenAccessPass(
        data.status ?? this.status,
        data.uniqueId ?? this.uniqueId,
        data.creatorId === undefined ? this.creatorId : data.creatorId,
        data.code ?? data.scanCode ?? this.code,
        data.businessRef ?? this.businessRef.clone(),
        data.creationDate ?? TimeZone.cloneDate(this.creationDate),
        data.templateId ?? this.templateId,
        data.orderId ?? this.orderId,
        data.viaOrderId ?? this.viaOrderId,
        data.viaProductId ?? this.viaProductId,
        data.viaGroupId ?? this.viaGroupId,
        data.storagePath ?? this.storagePath,
        data.originalName ?? this.originalName.clone(),
        data.overrideName ?? (this.overrideName?.clone() ?? null),
        data.price ?? this.price.clone(),
        data.originalPrice ?? (this.originalPrice?.clone() ?? null),
        data.protocol ?? cloneData(this.protocol),
        data.customer ?? cloneDataOrNull(this.customer),
        data.variation ?? cloneDataOrNull(this.variation),
        data.usedAt ?? TimeZone.cloneDateOrNull(this.usedAt),
        data.serialNumber ?? this.serialNumber,
        data.paymentMethod ?? this.paymentMethod,
        data.source ?? this.source,
        data.components ?? this.components.clone(),
    ) as this;
  }

  assignToCustomerAccount(account: CustomerAccountBase): this {
    return new OpenAccessPass(
        this.status,
        this.uniqueId,
        this.creatorId,
        this.code,
        this.businessRef.clone(),
        TimeZone.cloneDate(this.creationDate),
        this.templateId,
        this.orderId,
        this.viaOrderId,
        this.viaProductId,
        this.viaGroupId,
        this.storagePath,
        this.originalName.clone(),
        this.overrideName?.clone() ?? null,
        this.price.clone(),
        this.originalPrice?.clone() ?? null,
        cloneData(this.protocol),
        this.customer?.assignToCustomerAccount(account) ?? null,
        cloneDataOrNull(this.variation),
        TimeZone.cloneDateOrNull(this.usedAt),
        this.serialNumber,
        this.paymentMethod,
        this.source,
        this.components.clone(),
    ) as this;
  }

  getVariationId(): string | null {
    return this.variation?.uniqueId ?? null;
  }

  getVariationIdOrDefault(): string {
    return this.variation?.uniqueId ?? OpenAccessPass.DEFAULT_VARIATION_ID;
  }

  markAsUsed(now?: dayjs.Dayjs): void {
    this._status = OpenAccessPassStatus.Used;
    this._usedAt = now ?? TrueTime.now();
  }

  static encodedStatusSafe(status: OpenAccessPassStatus): string {
    return fromEnumAsString(status);
  }

  static generateQrCode(): string {
    return generateUniqueDatabaseId({ length: 20 });
  }

  static generateVoucherCode(): string {
    const randomStr = generateUniqueDatabaseId({ length: 15, alphabet: 'NONSIMILAR_CHARACTERS' });
    return `OFP-${randomStr.substring(0, 5)}-${randomStr.substring(5, 10)}-${randomStr.substring(10, randomStr.length)}`;
  }

  static generateStoragePath(
      passId: string,
      businessId: string,
      passName: string,
  ): string {
    const sanitizedPassName = toSafeFilename(passName).substring(0, 30);
    const filename = `${sanitizedPassName}_${passId}`;

    return `business_profiles/${businessId}/passes/${filename}.pdf`;
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof OpenAccessPass;
  }

  static assertType(dm: DataModel): dm is OpenAccessPass {
    return dm instanceof OpenAccessPass;
  }
}

export class OpenAccessPassBuilder extends DataModelBuilder<OpenAccessPass> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPass {
    return OpenAccessPass.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: OpenAccessPass): SerializedData | null {
    const protocol = model.protocol;
    if(!(protocol instanceof SeatedOpenAccessProtocol) && !(protocol instanceof StagedSeatedOpenAccessProtocol)) {
      return null;
    }

    const sources = OpenAccessPassSeatHelperFactory.forPass({
      protocol,
      passVariationId: model.getVariationIdOrDefault(),
    });
    const seats = sources.flatMap((source) => source.helper.findModifiedSeats());
    if(seats.length <= 0) {
      return null;
    }

    const seatRefs: string[] = [];
    for(const [eventId, seatId] of seats) {
      seatRefs.push(`${eventId}:${seatId}`);
    }

    return {
      'seats': seatRefs,
    };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, '$status', OpenAccessPassStatus, OpenAccessPassStatus.Live);

    if(versionRange.contains(OpenAccessPass.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OpenAccessPass.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [protocol] field of type [OpenAccessProtocol] was introduced
    if(version < 2) {
      const now = dayjs();

      // This will require a manual migration because the event list doesn't exist on the pass instance!
      map['protocol'] = context.toData(EventOpenAccessProtocol.forInstance({
        eventValidityRule: PassEventValidityRule.IncludedEvents,
        eventConfiguration: new Map<string, BooleanOrListOfString>(),
        maxEventCount: Cardinal.infinity,
        startDate: now,
        expirationDate: now.add(1000, 'year'),
      }));

      version = 2;
    }

    // In this version the [SCANNED] status was replaced with [USED]
    if(version < 3) {
      version = 3;
    }

    // In this version parameter `originalPrice` of type `Price | null` was introduced.
    if(version < 4) {
      version = 4;
    }

    // In this version parameter `orderId` of type `string | null` was introduced.
    if(version < 5) {
      version = 5;
    }

    // In this version parameter `storagePath` of type `string | null` was introduced.
    if(version < 6) {
      version = 6;
    }

    // In this version parameter `components` of type `ComponentList` was introduced.
    if(version < 7) {
      version = 7;
      map['components'] = [];
    }

    /**
     * Version 8:
     * - add `serialNumber` of type {@link string | null}.
     */
    if(version < 8) {
      version = 8;
    }

    /**
     * Version 9:
     * - add `scanCode` of type {@link string}.
     */
    if(version < 9) {
      version = 9;
      map['scanCode'] = map['uniqueId'];
    }

    /*
    * Version 10:
    * - rename `names` to `originalName`.
    * - add `overrideName` of type [LocalizedString | null].
    */
    if(version < 10) {
      version = 10;
      map['originalName'] = map['names'];
    }

    // Version 11:
    // - add `paymentMethod` of type `PaymentMethod | null`.
    if(version < 11) {
      version = 11;
    }

    // Version 12:
    // - add `source` of type `OpenAccessPassSource`.
    if(version < 12) {
      map['source'] = isNullOrUndefined(map['orderId']) ?
        fromEnumAsString(OpenAccessPassSource.Admin):
        fromEnumAsString(OpenAccessPassSource.Client);
      version = 12;
    }

    // In this version parameter `viaOrderId` of type `string | null` was introduced.
    if(version < 13) {
      map['viaOrderId'] = map['orderId'];
      map['orderId'] = null;
      version = 13;
    }

    // Version 14:
    // - add `variation` of type `OpenAccessPassVariationDetails | null`.
    if(version < 14) {
      version = 14;
    }

    // Version 15:
    // - rename `scanCode` to `code`.
    if(version < 15) {
      map['code'] = MapUtils.getString(map, 'scanCode');
      version = 15;
    }

    // Version 16:
    // - add `viaProductId` of type `string | null`.
    if(version < 16) {
      version = 16;
    }

    // Version 17:
    // - add `viaGroupId` of type `string | null`.
    if(version < 17) {
      version = 17;
    }

    // Version 18:
    // - add nullable creatorId; older passes do not record their creator.
    if(version < 18) {
      version = 18;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
